/**
 * Admin Projects Page
 *
 * Displays list of all projects with management options.
 * Allows creating new projects and navigating to project detail pages.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAdminProjects, getAdminProjectsStatsSummary, createAdminProject, deleteAdminProject, getAdminTags, type AdminProject, type AdminProjectSummaryStats, type AdminProjectTag, type CreateProjectRequest } from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

function formatPercent(numerator: number, denominator: number): string {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

interface StatCellProps {
  loading: boolean;
  primary: string;
  secondary?: string | null;
}

function StatCell({ loading, primary, secondary }: StatCellProps) {
  if (loading) {
    return (
      <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-gray-400">…</td>
    );
  }
  return (
    <td className="whitespace-nowrap px-4 py-4 text-right text-sm">
      <div className="text-gray-900">{primary}</div>
      {secondary && <div className="text-xs text-blue-600">{secondary}</div>}
    </td>
  );
}

export function AdminProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [tags, setTags] = useState<AdminProjectTag[]>([]);
  const [stats, setStats] = useState<Record<string, AdminProjectSummaryStats>>({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newProject, setNewProject] = useState<CreateProjectRequest>({
    name: '',
    slug: '',
    description: '',
    website_url: '',
    twitter_url: '',
    discord_url: '',
    image_url: '',
    banner_url: '',
    owner_address: '',
    tag_ids: [],
  });

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getAdminProjectsStatsSummary();
      setStats(data);
    } catch (err) {
      console.error('Failed to load project stats summary:', err);
      setStats({});
    } finally {
      setStatsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [projectsData, tagsData] = await Promise.all([
        getAdminProjects(),
        getAdminTags(),
      ]);
      setProjects(projectsData.projects);
      setTags(tagsData.tags);
      // Stats load in background so the main table renders immediately
      loadStats();
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreateLoading(true);
      setCreateError(null);

      // Clean up empty strings to null
      const projectData: CreateProjectRequest = {
        name: newProject.name,
        slug: newProject.slug || undefined,
        description: newProject.description || null,
        website_url: newProject.website_url || null,
        twitter_url: newProject.twitter_url || null,
        discord_url: newProject.discord_url || null,
        image_url: newProject.image_url || null,
        banner_url: newProject.banner_url || null,
        owner_address: newProject.owner_address || null,
        tag_ids: newProject.tag_ids?.length ? newProject.tag_ids : undefined,
      };

      await createAdminProject(projectData);
      await loadData();
      setShowCreateModal(false);
      setNewProject({
        name: '',
        slug: '',
        description: '',
        website_url: '',
        twitter_url: '',
        discord_url: '',
        image_url: '',
        banner_url: '',
        owner_address: '',
        tag_ids: [],
      });
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create project');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (!confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteAdminProject(projectId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete project');
    }
  };

  const toggleTag = (tagId: string) => {
    setNewProject(prev => ({
      ...prev,
      tag_ids: prev.tag_ids?.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...(prev.tag_ids || []), tagId],
    }));
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage project hubs that group collections and tokens
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              + New Project
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Total Projects</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{projects.length}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">With Collections</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {projects.filter(p => (p.collection_count ?? 0) > 0).length}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Available Tags</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{tags.length}</div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && projects.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading projects...</p>
            </div>
          </div>
        )}

        {/* Projects Table */}
        {!loading && projects.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">No projects found. Create your first project to get started.</p>
          </div>
        ) : projects.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Tags
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Collections
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Holders
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                    title="NFTs with an active sell listing / total NFTs"
                  >
                    Listed
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                    title="Holders who have an XRP Deals account"
                  >
                    Deals Users
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                    title="Holders with no un-migrated listings on other marketplaces"
                  >
                    Copied
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {projects.map((project) => {
                  const projectStats = stats[project.id];
                  const statsPlaceholder = statsLoading && !projectStats;
                  return (
                    <tr
                      key={project.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/projects/${project.id}`)}
                    >
                      <td className="whitespace-nowrap px-4 py-4">
                        <div className="flex items-center gap-3">
                          {project.image_url ? (
                            <img
                              src={project.image_url}
                              alt={project.name}
                              className="h-10 w-10 rounded-md object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100 text-gray-500 font-bold">
                              {project.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{project.name}</div>
                            <div className="text-sm text-gray-500">/{project.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {project.tags?.slice(0, 3).map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                            >
                              {tag.name}
                            </span>
                          ))}
                          {(project.tags?.length ?? 0) > 3 && (
                            <span className="inline-flex items-center text-xs text-gray-500">
                              +{(project.tags?.length ?? 0) - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-gray-900">
                        {project.collection_count ?? 0}
                      </td>
                      <StatCell
                        loading={statsPlaceholder}
                        primary={projectStats ? projectStats.total_holders.toLocaleString() : '—'}
                      />
                      <StatCell
                        loading={statsPlaceholder}
                        primary={
                          projectStats
                            ? `${projectStats.listed_nfts.toLocaleString()} / ${projectStats.total_nfts.toLocaleString()}`
                            : '—'
                        }
                        secondary={projectStats ? formatPercent(projectStats.listed_nfts, projectStats.total_nfts) : null}
                      />
                      <StatCell
                        loading={statsPlaceholder}
                        primary={projectStats ? projectStats.xrp_deals_users.toLocaleString() : '—'}
                        secondary={
                          projectStats ? formatPercent(projectStats.xrp_deals_users, projectStats.total_holders) : null
                        }
                      />
                      <StatCell
                        loading={statsPlaceholder}
                        primary={projectStats ? projectStats.copied_listings_users.toLocaleString() : '—'}
                        secondary={
                          projectStats ? formatPercent(projectStats.copied_listings_users, projectStats.total_holders) : null
                        }
                      />
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                        {new Date(project.created_at).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id, project.name);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Create New Project</h2>

              {createError && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {createError}
                </div>
              )}

              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={newProject.name}
                    onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Project name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Slug</label>
                  <input
                    type="text"
                    value={newProject.slug}
                    onChange={(e) => setNewProject(prev => ({ ...prev, slug: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="project-slug (auto-generated if blank)"
                    pattern="[a-z0-9-]+"
                  />
                  <p className="mt-1 text-xs text-gray-500">Lowercase letters, numbers, and hyphens only</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newProject.description || ''}
                    onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Brief description of the project"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website URL</label>
                    <input
                      type="url"
                      value={newProject.website_url || ''}
                      onChange={(e) => setNewProject(prev => ({ ...prev, website_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">X URL</label>
                    <input
                      type="url"
                      value={newProject.twitter_url || ''}
                      onChange={(e) => setNewProject(prev => ({ ...prev, twitter_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="https://x.com/..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Discord URL</label>
                  <input
                    type="url"
                    value={newProject.discord_url || ''}
                    onChange={(e) => setNewProject(prev => ({ ...prev, discord_url: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="https://discord.gg/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Owner Address</label>
                  <input
                    type="text"
                    value={newProject.owner_address || ''}
                    onChange={(e) => setNewProject(prev => ({ ...prev, owner_address: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="r..."
                  />
                  <p className="mt-1 text-xs text-gray-500">XRPL address of the project owner</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Image URL</label>
                    <p className="text-xs text-gray-500">Square format (1:1), e.g. 500x500px</p>
                    <input
                      type="url"
                      value={newProject.image_url || ''}
                      onChange={(e) => setNewProject(prev => ({ ...prev, image_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                    <p className="mt-1 text-xs text-gray-500">You can upload images after creating the project</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Banner URL</label>
                    <p className="text-xs text-gray-500">Wide format (~4:1), e.g. 1200x300px</p>
                    <input
                      type="url"
                      value={newProject.banner_url || ''}
                      onChange={(e) => setNewProject(prev => ({ ...prev, banner_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                    <p className="mt-1 text-xs text-gray-500">You can upload images after creating the project</p>
                  </div>
                </div>

                {tags.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                            newProject.tag_ids?.includes(tag.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateError(null);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create Project'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
